
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  UserCheck, 
  UserX, 
  Users, 
  Search, 
  Mail, 
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/hooks/auth';
import { toast } from '@/components/ui/sonner';

interface PendingUser {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  created_at: string;
  status: 'pending' | 'approved' | 'rejected';
  profile?: {
    name: string;
    email: string;
  };
}

const EnhancedApprovalInterface: React.FC = () => {
  const { user, hasAccess } = useSupabaseAuth();
  const queryClient = useQueryClient();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch pending approval requests
  const { data: pendingUsers = [], isLoading, error } = useQuery({
    queryKey: ['pending-approvals'],
    queryFn: async () => {
      console.log('Fetching pending approval requests...');
      
      const { data, error } = await supabase
        .from('user_approval_requests')
        .select(`
          id,
          user_id,
          created_at,
          status,
          profiles!inner(
            name,
            email
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching pending users:', error);
        throw error;
      }

      console.log('Raw pending users data:', data);

      // Transform the data to match our interface
      const transformedData: PendingUser[] = (data || []).map(item => ({
        id: item.id,
        user_id: item.user_id,
        user_name: (item.profiles as any)?.name || 'Unknown',
        user_email: (item.profiles as any)?.email || 'Unknown',
        created_at: item.created_at,
        status: item.status as 'pending' | 'approved' | 'rejected',
        profile: {
          name: (item.profiles as any)?.name || 'Unknown',
          email: (item.profiles as any)?.email || 'Unknown'
        }
      }));

      console.log('Transformed pending users:', transformedData);
      return transformedData;
    },
    enabled: !!user && hasAccess('admin'),
  });

  // Approve user mutation
  const approveUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      console.log('Approving user:', userId);
      
      const { data, error } = await supabase.rpc('admin_approve_user_safe', {
        target_user_id: userId,
        reviewer_id: user?.id || ''
      });

      if (error) {
        console.error('Error approving user:', error);
        throw error;
      }

      console.log('Approval response:', data);
      
      // Check if the response indicates success
      if (typeof data === 'object' && data !== null && 'success' in data) {
        const result = data as { success?: boolean; error?: string };
        if (!result.success) {
          throw new Error(result.error || 'Failed to approve user');
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      toast.success('User approved successfully');
    },
    onError: (error) => {
      console.error('Approval error:', error);
      toast.error(`Failed to approve user: ${error.message}`);
    },
  });

  // Reject user mutation  
  const rejectUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      console.log('Rejecting user:', userId);
      
      const { data, error } = await supabase.rpc('admin_reject_user_safe', {
        target_user_id: userId,
        reviewer_id: user?.id || ''
      });

      if (error) {
        console.error('Error rejecting user:', error);
        throw error;
      }

      console.log('Rejection response:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      toast.success('User rejected successfully');
    },
    onError: (error) => {
      console.error('Rejection error:', error);
      toast.error(`Failed to reject user: ${error.message}`);
    },
  });

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      setIsProcessing(true);
      const results = [];
      
      for (const userId of userIds) {
        try {
          const result = await approveUserMutation.mutateAsync(userId);
          results.push({ userId, success: true, result });
        } catch (error) {
          results.push({ userId, success: false, error });
        }
      }
      
      return results;
    },
    onSuccess: (results) => {
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      if (successful > 0) {
        toast.success(`Successfully approved ${successful} user(s)`);
      }
      if (failed > 0) {
        toast.error(`Failed to approve ${failed} user(s)`);
      }
      
      setSelectedUsers([]);
      setIsProcessing(false);
    },
    onError: () => {
      setIsProcessing(false);
    },
  });

  const filteredUsers = pendingUsers.filter(user =>
    user.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(filteredUsers.map(user => user.user_id));
    } else {
      setSelectedUsers([]);
    }
  };

  if (!hasAccess('admin')) {
    return (
      <Alert>
        <AlertDescription>
          You don't have permission to access user approvals.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading pending approvals...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Error loading pending approvals: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Approval Management
          </CardTitle>
          <CardDescription>
            Review and approve pending user registration requests
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and bulk actions */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {selectedUsers.length > 0 && (
              <div className="flex gap-2">
                <Button
                  onClick={() => bulkApproveMutation.mutate(selectedUsers)}
                  disabled={isProcessing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Approve Selected ({selectedUsers.length})
                </Button>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="font-medium">Pending</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{filteredUsers.length}</p>
            </div>
          </div>

          <Separator />

          {/* User list */}
          {filteredUsers.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-4">
                <Checkbox
                  checked={selectedUsers.length === filteredUsers.length}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm font-medium">Select All</span>
              </div>

              {filteredUsers.map((pendingUser) => (
                <Card key={pendingUser.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedUsers.includes(pendingUser.user_id)}
                        onCheckedChange={(checked) => 
                          handleSelectUser(pendingUser.user_id, checked as boolean)
                        }
                      />
                      <div>
                        <h4 className="font-medium">{pendingUser.user_name}</h4>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          <span>{pendingUser.user_email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>Applied: {new Date(pendingUser.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                      
                      <Button
                        size="sm"
                        onClick={() => approveUserMutation.mutate(pendingUser.user_id)}
                        disabled={approveUserMutation.isPending}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {approveUserMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => rejectUserMutation.mutate(pendingUser.user_id)}
                        disabled={rejectUserMutation.isPending}
                      >
                        {rejectUserMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No pending approvals</h3>
              <p className="text-muted-foreground">All user requests have been processed.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedApprovalInterface;
